import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { UsersIcon, UserAddIcon } from "@heroicons/react/solid";
import useMountStatus from "../../../../hooks/useMountStatus";

import { getOrganisationUsers } from "../../../../utils/api";
import { NO_ACCESS_TYPES, PERMISSIONS } from "../../../../utils/constants";
import { DashboardContext } from "../../../../context/DashboardContext";

import Alert, { ALERT_TYPE } from "../../../Auth/Alert";
import UserRow from "./UserRow";
import DashboardContainer from "../../../SharedComponents/DashboardContainer";
import AccessControl from "../../../SharedComponents/AccessControl";
import PaginatedSearch from "../../../SharedComponents/PaginatedSearch";

function Users() {
    const { searchTerm } = useParams();

    const [alert, setAlert] = useState("");
    const [alertType, setAlertType] = useState(ALERT_TYPE.ERROR);
    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(true);

    const unmounted = useMountStatus();

    const navigate = useNavigate();

    const context = useContext(DashboardContext);

    const fetchUsers = useCallback(async () => {
        setUsersLoading(true);
        try {
            const response = await getOrganisationUsers();
            if (unmounted) return;
            setUsers(response);
        } catch (err) {
            if (unmounted) return;
            setAlert(err.message);
            setAlertType(ALERT_TYPE.ERROR);
        }
        setUsersLoading(false);
    }, [unmounted]);

    const handleGoToAddUser = () => {
        navigate("/users/add-user");
    };

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    return (
        <AccessControl requiredPermission={PERMISSIONS.VIEW_USERS_LIST} noAccessType={NO_ACCESS_TYPES.REDIRECT}>
            <DashboardContainer
                title={"Users"}
                titleIcon={<UsersIcon className="block h-5 w-5 mr-2" />}
                actionButton={context.userPermissions.includes(PERMISSIONS.ACCESS_SETUP_USER_PAGE)}
                actionButtonText={"Add User"}
                actionButtonIcon={<UserAddIcon className="block h-4 w-4 mr-2" />}
                actionButtonOnClick={handleGoToAddUser}
                actionButtonPermission={PERMISSIONS.CREATE_USER_ACCOUNTS}
            >
                <Alert text={alert} type={alertType} active={alert} close={() => setAlert("")} />
                <PaginatedSearch
                    loading={usersLoading}
                    items={users}
                    itemName={"Users"}
                    searchAttributes={["firstName", "lastName", "emailAddress"]}
                    columnNames={
                        context.userPermissions.includes(PERMISSIONS.ASSIGN_ASSESSMENTS)
                            ? ["Details", "User groups", "Assign assessment", "Edit"]
                            : ["Details", "User groups", "Edit"]
                    }
                    ItemRow={UserRow}
                    initialSearchValue={searchTerm}
                />
            </DashboardContainer>
        </AccessControl>
    );
}

export default Users;
